import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { listCache } from '../utils/listCache';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  MdAdd, MdLocationOn, MdPerson, MdPhotoLibrary, MdCalendarToday,
  MdGroups, MdChevronRight, MdAccessTime
} from 'react-icons/md';

const AddIcon = MdAdd as any;
const LocationIcon = MdLocationOn as any;
const PersonIcon = MdPerson as any;
const PhotoIcon = MdPhotoLibrary as any;
const CalendarIcon = MdCalendarToday as any;
const GroupsIcon = MdGroups as any;
const ChevronIcon = MdChevronRight as any;
const TimeIcon = MdAccessTime as any;

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

export const EventActivity: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t, language } = useLanguage();

  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const restoredRef = useRef(false);
  const isLoadingMoreRef = useRef(false);

  useEffect(() => {
    setTimeout(() => { window.scroll({ top: -1, left: 0, behavior: 'smooth' }); }, 10);
  }, [pathname]);

  const fetchActivities = useCallback(async (currentOffset: number) => {
    if (isLoadingMoreRef.current && currentOffset > 0) return;
    try {
      if (currentOffset === 0) setLoading(true);
      else isLoadingMoreRef.current = true;
      const res = await axiosInstance.get(`/event-activities?limit=10&offset=${currentOffset}`);
      const newData: any[] = res.data;
      if (newData.length < 10) setHasMore(false);
      else setHasMore(true);
      if (currentOffset === 0) {
        setActivities(newData);
      } else {
        setActivities(prev => {
          const ids = new Set(prev.map(a => a.id));
          return [...prev, ...newData.filter(a => !ids.has(a.id))];
        });
      }
    } catch (err) {
      console.error('Failed to fetch event activities:', err);
      if (currentOffset === 0) setActivities([]);
    } finally {
      setLoading(false);
      isLoadingMoreRef.current = false;
    }
  }, []);

  useEffect(() => {
    const cached = listCache.restore('event-activities');
    if (cached) {
      setActivities(cached.items);
      setOffset(cached.cursor);
      setHasMore(cached.hasMore);
      setLoading(false);
      restoredRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById('scrollableDiv');
          if (el) el.scrollTop = cached.scrollTop;
        });
      });
      listCache.clear('event-activities');
      setTimeout(() => { restoredRef.current = false; }, 0);
      return;
    }
    setOffset(0);
    fetchActivities(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (restoredRef.current) return;
    if (offset > 0) fetchActivities(offset);
  }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

  const parseDate = (dateStr: string) => new Date(dateStr.slice(0, 10) + 'T00:00:00');

  const getMonthLabel = (dateStr: string) =>
    parseDate(dateStr).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { month: 'short' }).toUpperCase();

  const getDayLabel = (dateStr: string) => parseDate(dateStr).getDate();

  const getTimeLabel = (dateStr: string) => {
    if (!dateStr || dateStr.length < 16) return '';
    const d = new Date(dateStr.slice(0, 16)); // "YYYY-MM-DDTHH:MM" → local time
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const handleCardClick = (act: any) => {
    const el = document.getElementById('scrollableDiv');
    listCache.save('event-activities', { items: activities, cursor: offset, hasMore, scrollTop: el?.scrollTop || 0 });
    navigate('/DetailEventActivity', { state: { activityId: act.id } });
  };

  const renderCard = (act: any) => {
    const images: string[] = act.images || [];
    const thumbUrl = images.length > 0 ? `${IMAGE_BASE_URL}${images[0]}` : null;
    const participantCount = act.participant_count || 0;

    return (
      <div
        key={act.id}
        onClick={() => handleCardClick(act)}
        style={{
          display: 'flex',
          backgroundColor: '#fff',
          borderRadius: 16,
          overflow: 'hidden',
          border: '1px solid #E5E7EB',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          cursor: 'pointer',
          marginBottom: 14,
          minHeight: 100,
        }}
      >
        {/* Date badge sidebar */}
        <div style={{
          width: 64,
          flexShrink: 0,
          background: 'linear-gradient(180deg, #6366F1 0%, #4F46E5 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px 0',
          gap: 2,
        }}>
          <text.T14 style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10, fontWeight: '700', letterSpacing: 1 }}>
            {getMonthLabel(act.date)}
          </text.T14>
          <div style={{ color: '#fff', fontSize: 28, fontWeight: '800', lineHeight: 1 }}>
            {getDayLabel(act.date)}
          </div>
          {getTimeLabel(act.date) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 4 }}>
              <TimeIcon size={9} color="rgba(255,255,255,0.75)" />
              <text.T14 style={{ color: 'rgba(255,255,255,0.85)', fontSize: 10, fontWeight: '600' }}>
                {getTimeLabel(act.date)}
              </text.T14>
            </div>
          )}
          {thumbUrl && (
            <div style={{ marginTop: 8, width: 28, height: 28, borderRadius: 6, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.3)' }}>
              <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '12px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
          <div>
            {act.description ? (
              <text.T14 style={{
                color: theme.colors.mainDark, lineHeight: 1.5,
                display: '-webkit-box' as any,
                overflow: 'hidden', textOverflow: 'ellipsis',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical' as any,
                marginBottom: 8,
                textTransform: 'capitalize'
              }}>
                {act.description}
              </text.T14>
            ) : (
              <text.T14 style={{ color: '#9CA3AF', fontStyle: 'italic', marginBottom: 8, display: 'block' }}>
                {t.common.description}
              </text.T14>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {act.address && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, maxWidth: '60%' }}>
                <LocationIcon size={12} color="#6B7280" />
                <text.T14 style={{
                  fontSize: 11, color: '#6B7280',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {act.address}
                </text.T14>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
              {images.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <PhotoIcon size={12} color="#6366F1" />
                  <text.T14 style={{ fontSize: 11, color: '#6366F1', fontWeight: '600' }}>
                    {images.length}
                  </text.T14>
                </div>
              )}
              {participantCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <GroupsIcon size={12} color="#10B981" />
                  <text.T14 style={{ fontSize: 11, color: '#10B981', fontWeight: '600' }}>
                    {participantCount}
                  </text.T14>
                </div>
              )}
              {act.user_name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <PersonIcon size={12} color="#9CA3AF" />
                  <text.T14 style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'capitalize' }}>
                    {act.user_name}
                  </text.T14>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Arrow */}
        <div style={{ display: 'flex', alignItems: 'center', paddingRight: 8 }}>
          <ChevronIcon size={20} color="#D1D5DB" />
        </div>
      </div>
    );
  };

  const renderHeader = () => (
    <components.Header
      title={(t as any).eventActivity?.title || 'Event Activities'}
      goBack={true}
      plus={true}
      onPlus={() => navigate('/CreateEventActivity')}
    />
  );

  const renderContent = () => (
    <main
      id="scrollableDiv"
      className="container"
      style={{ marginTop: 60, paddingBottom: 100, height: 'calc(100vh - 60px)', overflowY: 'auto' }}
    >
      {/* Header stats strip */}
      {!loading && activities.length > 0 && (
        <div style={{
          display: 'flex', gap: 12, marginBottom: 20, marginTop: 16,
          padding: '14px 16px',
          background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
          borderRadius: 14,
          color: 'white',
          alignItems: 'center',
        }}>
          <div style={{ flex: 1 }}>
            <text.T14 style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
              {(t as any).eventActivity?.title || 'Event Activities'}
            </text.T14>
            <text.H4 style={{ color: 'white', marginTop: 2 }}>
              {activities.length}{hasMore ? '+' : ''} Event{activities.length !== 1 ? 's' : ''}
            </text.H4>
          </div>
          <div
            onClick={() => navigate('/CreateEventActivity')}
            style={{
              width: 42, height: 42, borderRadius: 12,
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', border: '1px solid rgba(255,255,255,0.3)'
            }}
          >
            <AddIcon size={22} color="white" />
          </div>
        </div>
      )}

      {loading ? (
        <text.T16 style={{ textAlign: 'center', marginTop: 60, color: '#9CA3AF' }}>
          {(t as any).eventActivity?.loading || 'Loading events...'}
        </text.T16>
      ) : activities.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 24px' }}>
          <div style={{
            width: 80, height: 80, borderRadius: 24,
            background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <CalendarIcon size={38} color="#6366F1" />
          </div>
          <text.H5 style={{ color: theme.colors.mainDark, marginBottom: 8 }}>
            {(t as any).eventActivity?.noActivities || 'No Events Yet'}
          </text.H5>
          <text.T14 style={{ color: '#9CA3AF', marginBottom: 28, lineHeight: 1.5 }}>
            {(t as any).eventActivity?.noActivitiesDesc || "You haven't created any events yet."}
          </text.T14>
          <div
            onClick={() => navigate('/CreateEventActivity')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
              color: 'white', padding: '12px 24px', borderRadius: 12,
              cursor: 'pointer', fontWeight: '600', fontSize: 14,
            }}
          >
            <AddIcon size={18} color="white" />
            {(t as any).eventActivity?.submit || 'Create Event'}
          </div>
        </div>
      ) : (
        <div style={{ paddingTop: 4 }}>
          <InfiniteScroll
            dataLength={activities.length}
            next={() => setOffset(prev => prev + 10)}
            hasMore={hasMore}
            loader={
              <div style={{ padding: '10px 0' }}>
                <text.T14 style={{ color: '#9CA3AF', textAlign: 'center' }}>
                  {(t as any).eventActivity?.loadingMore || 'Loading more...'}
                </text.T14>
              </div>
            }
            endMessage={
              <div style={{ padding: '10px 0', marginBottom: 20 }}>
                <text.T14 style={{ color: '#9CA3AF', textAlign: 'center' }}>
                  {(t as any).eventActivity?.allLoaded || 'All events loaded'}
                </text.T14>
              </div>
            }
            scrollableTarget="scrollableDiv"
            style={{ overflow: 'visible' }}
          >
            {activities.map(act => renderCard(act))}
          </InfiniteScroll>
        </div>
      )}
    </main>
  );

  return (
    <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <style>{`#screen { --scrollbar-width: 0px; } ::-webkit-scrollbar { display: none; }`}</style>
      {renderHeader()}
      {renderContent()}
    </div>
  );
};
